/**
 * MCP Skills Admin View
 *
 * Manage database-backed skills: list, create, edit, delete, search.
 * Skills have 3 instruction sections: Core, Admin, and Bot.
 */

import { html, nothing } from "lit";

export type SkillEntry = {
  id: string;
  projectId: string | null;
  name: string;
  description: string;
  coreInstruction: string;
  adminInstruction: string;
  botInstruction: string;
  executionType?: string;
  templateType?: string;
  metadata: Record<string, unknown>;
  isActive: boolean;
  version: number;
  createdAt: string | Date;
  updatedAt: string | Date;
  /** Whether this skill is exposed as a user /command. undefined = auto (respects frontmatter). */
  command?: boolean;
};

export type SkillStatusFilter = "all" | "active" | "inactive";
export type SkillCategoryFilter = "all" | "skill" | "skill_template" | "cron_job" | "task" | "office_template";

export type SkillsAdminProps = {
  loading: boolean;
  error: string | null;
  skills: SkillEntry[];
  searchQuery: string;
  statusFilter: SkillStatusFilter;
  categoryFilter: SkillCategoryFilter;
  selectedSkill: SkillEntry | null;
  editMode: boolean;
  editForm: Partial<SkillEntry> | null;
  createMode: boolean;
  projects: Array<{ id: string; name: string }>;
  onRefresh: () => void;
  onSearchChange: (query: string) => void;
  onStatusFilterChange: (filter: SkillStatusFilter) => void;
  onCategoryFilterChange: (filter: SkillCategoryFilter) => void;
  onSelectSkill: (id: string) => void;
  onCreateSkill: () => void;
  onEditSkill: (id: string) => void;
  onSaveSkill: () => void;
  onCancelEdit: () => void;
  onToggleActive: (id: string, active: boolean) => void;
  onToggleCommand: (skillKey: string, command: boolean) => void;
  onDeleteSkill: (id: string) => void;
  onFormChange: (field: string, value: unknown) => void;
  onSyncFromFilesystem?: () => void;
  syncLoading?: boolean;
  syncResult?: { imported: number; skipped: number } | null;
};

const CATEGORY_LABELS: Record<string, string> = {
  skill: "Skill",
  skill_template: "Skill Template",
  cron_job: "Cron Job Template",
  task: "Task Template",
  office_template: "Office Template",
};

function categoryLabel(templateType?: string): string {
  return CATEGORY_LABELS[templateType ?? "skill"] ?? "Skill";
}

function categoryPillClass(templateType?: string): string {
  switch (templateType) {
    case "skill_template":
      return "pill--warn";
    case "cron_job":
      return "pill--info";
    case "task":
      return "pill--info";
    case "office_template":
      return "pill--info";
    default:
      return "";
  }
}

export function renderSkillsAdmin(props: SkillsAdminProps) {
  // Apply category filter, then status filter, then search filter
  let filtered = props.skills;
  if (props.categoryFilter !== "all") {
    filtered = filtered.filter((s) => (s.templateType ?? "skill") === props.categoryFilter);
  }
  if (props.statusFilter === "active") {
    filtered = filtered.filter((s) => s.isActive);
  } else if (props.statusFilter === "inactive") {
    filtered = filtered.filter((s) => !s.isActive);
  }
  if (props.searchQuery) {
    const q = props.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (s) => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q),
    );
  }

  const activeCount = props.skills.filter((s) => s.isActive).length;
  const inactiveCount = props.skills.length - activeCount;

  const filterBtnStyle = (f: SkillStatusFilter) =>
    `padding:4px 10px;border:1px solid var(--border);border-radius:4px;cursor:pointer;font-size:0.85em;` +
    (props.statusFilter === f
      ? "background:var(--accent);color:var(--bg);border-color:var(--accent);"
      : "background:var(--bg-card);color:var(--fg);");

  return html`
    <section class="users-layout">
      <!-- Skills List -->
      <div class="card">
        <div class="card-title">
          Skills
          <span class="muted">(${filtered.length}${filtered.length !== props.skills.length ? ` of ${props.skills.length}` : ""})</span>
        </div>

        <div style="display:flex;gap:8px;margin-bottom:8px;">
          <input
            type="text"
            placeholder="Search skills..."
            .value=${props.searchQuery}
            @input=${(e: Event) => props.onSearchChange((e.target as HTMLInputElement).value)}
            style="flex:1;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);"
          />
          <button class="btn btn--sm" @click=${props.onRefresh} ?disabled=${props.loading}>
            ${props.loading ? "Loading..." : "Refresh"}
          </button>
          ${
            props.onSyncFromFilesystem
              ? html`
                  <button
                    class="btn btn--sm"
                    @click=${props.onSyncFromFilesystem}
                    ?disabled=${props.syncLoading || props.loading}
                  >
                    ${props.syncLoading ? "Syncing..." : "Sync from Filesystem"}
                  </button>
                `
              : nothing
          }
          <button class="btn btn--sm btn--primary" @click=${props.onCreateSkill}>
            + New Skill
          </button>
        </div>

        <!-- Category filter -->
        <div style="display:flex;gap:6px;margin-bottom:8px;flex-wrap:wrap;">
          ${(["all", "skill", "skill_template", "cron_job", "task", "office_template"] as const).map((cat) => {
            const count =
              cat === "all"
                ? props.skills.length
                : props.skills.filter((s) => (s.templateType ?? "skill") === cat).length;
            const label = cat === "all" ? "All" : categoryLabel(cat);
            const isActive = props.categoryFilter === cat;
            return html`
                <button
                  style=${
                    "padding:4px 10px;border:1px solid var(--border);border-radius:4px;cursor:pointer;font-size:0.85em;" +
                    (isActive
                      ? "background:var(--accent);color:var(--bg);border-color:var(--accent);"
                      : "background:var(--bg-card);color:var(--fg);")
                  }
                  @click=${() => props.onCategoryFilterChange(cat)}
                >
                  ${label} (${count})
                </button>
              `;
          })}
        </div>

        <!-- Status filter -->
        <div style="display:flex;gap:6px;margin-bottom:12px;">
          <button style=${filterBtnStyle("all")} @click=${() => props.onStatusFilterChange("all")}>
            All (${props.skills.length})
          </button>
          <button style=${filterBtnStyle("active")} @click=${() => props.onStatusFilterChange("active")}>
            Active (${activeCount})
          </button>
          <button style=${filterBtnStyle("inactive")} @click=${() => props.onStatusFilterChange("inactive")}>
            Inactive (${inactiveCount})
          </button>
        </div>

        ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}
        ${
          props.syncResult
            ? html`
                <div class="callout info" style="margin-bottom: 8px;">
                  Sync complete: ${props.syncResult.imported} imported, ${props.syncResult.skipped} already existed.
                </div>
              `
            : nothing
        }

        <div style="max-height:600px;overflow-y:auto;">
          ${
            filtered.length === 0
              ? html`
                  <div class="muted" style="padding: 16px">No skills found.</div>
                `
              : filtered.map(
                  (skill) => html`
                  <div
                    style="padding:10px 12px;border:1px solid var(--border);border-radius:6px;margin-bottom:6px;cursor:pointer;
                           ${props.selectedSkill?.id === skill.id ? "border-color:var(--accent);" : ""}"
                    @click=${() => props.onSelectSkill(skill.id)}
                  >
                    <div style="display:flex;justify-content:space-between;align-items:center;">
                      <strong>${skill.name}</strong>
                      <div style="display:flex;gap:6px;align-items:center;">
                        <span class="pill pill--sm ${categoryPillClass(skill.templateType)}">
                          ${categoryLabel(skill.templateType)}
                        </span>
                        <span class="pill pill--sm ${skill.isActive ? "pill--ok" : ""}">
                          ${skill.isActive ? "Active" : "Inactive"}
                        </span>
                        <span class="muted" style="font-size:0.75em;">v${skill.version}</span>
                      </div>
                    </div>
                    <div class="muted" style="font-size:0.85em;margin-top:4px;">
                      ${
                        skill.description.length > 120
                          ? skill.description.slice(0, 120) + "..."
                          : skill.description
                      }
                    </div>
                  </div>
                `,
                )
          }
        </div>
      </div>

      <!-- Skill Detail / Edit Form -->
      <div class="card">
        ${
          props.editMode || props.createMode
            ? renderSkillForm(props)
            : props.selectedSkill
              ? renderSkillDetail(props)
              : html`
                  <div class="muted" style="padding: 24px; text-align: center">
                    Select a skill to view details, or click "+ New Skill" to create one.
                  </div>
                `
        }
      </div>
    </section>
  `;
}

const sectionPreStyle =
  "margin-top:4px;padding:12px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);overflow-x:auto;white-space:pre-wrap;font-size:0.85em;max-height:300px;overflow-y:auto;";

function renderSkillDetail(props: SkillsAdminProps) {
  const skill = props.selectedSkill!;
  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;">
      <div class="card-title">${skill.name}</div>
      <div style="display:flex;gap:6px;">
        <button class="btn btn--sm" @click=${() => props.onEditSkill(skill.id)}>Edit</button>
        <button
          class="btn btn--sm"
          @click=${() => props.onToggleActive(skill.id, !skill.isActive)}
        >
          ${skill.isActive ? "Deactivate" : "Activate"}
        </button>
        <button
          class="btn btn--sm"
          @click=${() => {
            const next = skill.command === true ? false : true;
            props.onToggleCommand(skill.name, next);
          }}
        >
          ${skill.command === undefined ? "/command: auto" : skill.command ? "/command: on" : "/command: off"}
        </button>
        <button
          class="btn btn--sm"
          style="color:var(--danger,#e55);"
          @click=${() => props.onDeleteSkill(skill.id)}
        >
          Delete
        </button>
      </div>
    </div>

    <div class="detail-stat-grid" style="margin:12px 0;">
      <div class="stat">
        <div class="stat-value">${skill.isActive ? "Active" : "Inactive"}</div>
        <div class="stat-label">Status</div>
      </div>
      <div class="stat">
        <div class="stat-value">v${skill.version}</div>
        <div class="stat-label">Version</div>
      </div>
      <div class="stat">
        <div class="stat-value">${skill.projectId ?? "Global"}</div>
        <div class="stat-label">Scope</div>
      </div>
      <div class="stat">
        <div class="stat-value">${categoryLabel(skill.templateType)}</div>
        <div class="stat-label">Category</div>
      </div>
      <div class="stat">
        <div class="stat-value">${skill.executionType === "system_script" ? "System Script" : "Prompt"}</div>
        <div class="stat-label">Execution Type</div>
      </div>
    </div>

    <div style="margin-bottom:12px;">
      <strong>Description:</strong>
      <div class="muted" style="margin-top:4px;">${skill.description}</div>
    </div>

    <div style="margin-bottom:12px;">
      <strong>Core Instruction:</strong>
      ${
        skill.coreInstruction
          ? html`<pre style=${sectionPreStyle}>${skill.coreInstruction}</pre>`
          : html`
              <div class="muted" style="margin-top: 4px; font-style: italic">Empty</div>
            `
      }
    </div>

    <div style="margin-bottom:12px;">
      <strong>Admin Instruction:</strong>
      ${
        skill.adminInstruction
          ? html`<pre style=${sectionPreStyle}>${skill.adminInstruction}</pre>`
          : html`
              <div class="muted" style="margin-top: 4px; font-style: italic">Empty</div>
            `
      }
    </div>

    <div style="margin-bottom:12px;">
      <strong>Bot Instruction:</strong>
      ${
        skill.botInstruction
          ? html`<pre style=${sectionPreStyle}>${skill.botInstruction}</pre>`
          : html`
              <div class="muted" style="margin-top: 4px; font-style: italic">Empty</div>
            `
      }
    </div>

    ${
      Object.keys(skill.metadata ?? {}).length > 0
        ? html`
          <div style="margin-bottom:12px;">
            <strong>Metadata:</strong>
            <pre style="margin-top:4px;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.85em;">${JSON.stringify(skill.metadata, null, 2)}</pre>
          </div>
        `
        : nothing
    }

    <div class="muted" style="font-size:0.8em;">
      Created: ${formatDate(skill.createdAt)}
      · Updated: ${formatDate(skill.updatedAt)}
    </div>
  `;
}

function renderSkillForm(props: SkillsAdminProps) {
  const form = props.editForm ?? {};
  const isNew = props.createMode;
  const fieldStyle =
    "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);margin-top:4px;";
  const textareaStyle = fieldStyle + "min-height:150px;font-family:monospace;resize:vertical;";

  return html`
    <div class="card-title">${isNew ? "Create New Skill" : "Edit Skill"}</div>

    <div style="display:flex;flex-direction:column;gap:12px;margin-top:12px;">
      <label>
        <strong>Name</strong>
        <input
          type="text"
          .value=${(form.name as string) ?? ""}
          @input=${(e: Event) => props.onFormChange("name", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="my-skill-name (lowercase, hyphens)"
        />
      </label>

      <label>
        <strong>Project</strong>
        <select
          .value=${(form.projectId as string) ?? ""}
          @change=${(e: Event) =>
            props.onFormChange("projectId", (e.target as HTMLSelectElement).value || null)}
          style=${fieldStyle}
        >
          <option value="">Global (no project)</option>
          ${props.projects.map(
            (p) =>
              html`<option value=${p.id} ?selected=${form.projectId === p.id}>
                ${p.name}
              </option>`,
          )}
        </select>
      </label>

      <label>
        <strong>Execution Type</strong>
        <select
          .value=${(form.executionType as string) || "prompt"}
          @change=${(e: Event) =>
            props.onFormChange("executionType", (e.target as HTMLSelectElement).value)}
          style=${fieldStyle}
        >
          <option value="prompt" ?selected=${form.executionType === "prompt" || !form.executionType}>Prompt (LLM Pipeline)</option>
          <option value="system_script" ?selected=${form.executionType === "system_script"}>System Script (MCP Execute)</option>
        </select>
      </label>

      <label>
        <strong>Category</strong>
        <select
          .value=${(form.templateType as string) || "skill"}
          @change=${(e: Event) =>
            props.onFormChange("templateType", (e.target as HTMLSelectElement).value)}
          style=${fieldStyle}
        >
          <option value="skill" ?selected=${(form.templateType ?? "skill") === "skill"}>Skill (Regular)</option>
          <option value="skill_template" ?selected=${form.templateType === "skill_template"}>Skill Template</option>
          <option value="cron_job" ?selected=${form.templateType === "cron_job"}>Cron Job Template</option>
          <option value="task" ?selected=${form.templateType === "task"}>Task Template</option>
          <option value="office_template" ?selected=${form.templateType === "office_template"}>Office Template</option>
        </select>
      </label>

      <label>
        <strong>Description</strong>
        <input
          type="text"
          .value=${(form.description as string) ?? ""}
          @input=${(e: Event) =>
            props.onFormChange("description", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="Brief description (used as trigger text)"
        />
      </label>

      <label>
        <strong>Core Instruction</strong>
        <div class="muted" style="font-size:0.85em;">The base skill content -- what it teaches and how it works.</div>
        <textarea
          .value=${(form.coreInstruction as string) ?? ""}
          @input=${(e: Event) =>
            props.onFormChange("coreInstruction", (e.target as HTMLTextAreaElement).value)}
          style=${textareaStyle}
          placeholder="Core skill content (markdown)"
        ></textarea>
      </label>

      <label>
        <strong>Admin Instruction</strong>
        <div class="muted" style="font-size:0.85em;">Admin-customizable instructions for tailoring the skill.</div>
        <textarea
          .value=${(form.adminInstruction as string) ?? ""}
          @input=${(e: Event) =>
            props.onFormChange("adminInstruction", (e.target as HTMLTextAreaElement).value)}
          style=${textareaStyle}
          placeholder="Admin instructions (optional)"
        ></textarea>
      </label>

      <label>
        <strong>Bot Instruction</strong>
        <div class="muted" style="font-size:0.85em;">How the bot should behave when using this skill.</div>
        <textarea
          .value=${(form.botInstruction as string) ?? ""}
          @input=${(e: Event) =>
            props.onFormChange("botInstruction", (e.target as HTMLTextAreaElement).value)}
          style=${textareaStyle}
          placeholder="Bot behavior instructions (optional)"
        ></textarea>
      </label>

      <div style="display:flex;gap:16px;flex-wrap:wrap;align-items:flex-start;">
        <label style="display:flex;flex-direction:column;gap:4px;">
          <strong style="display:flex;align-items:center;gap:6px;">
            <input
              type="checkbox"
              .checked=${(form.metadata as Record<string, unknown>)?.showInConsole === true}
              @change=${(e: Event) => {
                const meta = { ...(form.metadata as Record<string, unknown>) };
                meta.showInConsole = (e.target as HTMLInputElement).checked;
                props.onFormChange("metadata", meta);
              }}
            />
            Show in Console
          </strong>
          <div class="muted" style="font-size:0.85em;">Display this skill as a button in the chat console bar.</div>
        </label>
        <label style="flex:1;min-width:180px;">
          <strong>Console Label</strong>
          <div class="muted" style="font-size:0.85em;">Button text (defaults to skill name if empty).</div>
          <input
            type="text"
            .value=${(form.metadata as Record<string, unknown>)?.consoleLabel as string ?? ""}
            @input=${(e: Event) => {
              const meta = { ...(form.metadata as Record<string, unknown>) };
              meta.consoleLabel = (e.target as HTMLInputElement).value;
              props.onFormChange("metadata", meta);
            }}
            placeholder="e.g. Summarize"
            style=${fieldStyle}
          />
        </label>
      </div>

      <label>
        <strong>Metadata (JSON)</strong>
        <textarea
          .value=${JSON.stringify(form.metadata ?? {}, null, 2)}
          @input=${(e: Event) => {
            try {
              const parsed = JSON.parse((e.target as HTMLTextAreaElement).value);
              props.onFormChange("metadata", parsed);
            } catch {
              // Invalid JSON, ignore until valid
            }
          }}
          style=${fieldStyle + "min-height:60px;font-family:monospace;resize:vertical;"}
          placeholder=${'{"emoji": "...", "requires": ["..."]}'}
        ></textarea>
      </label>

      <div style="display:flex;gap:8px;margin-top:8px;">
        <button class="btn btn--primary" @click=${props.onSaveSkill}>
          ${isNew ? "Create" : "Save"}
        </button>
        <button class="btn" @click=${props.onCancelEdit}>Cancel</button>
      </div>
    </div>
  `;
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) {
    return "\u2014";
  }
  try {
    const str = typeof value === "string" ? value : value.toISOString();
    return str.split("T")[0] ?? "\u2014";
  } catch {
    return String(value);
  }
}
